<h2 mat-dialog-title>Request a court change</h2>

@if (!submitted) {
  <mat-dialog-content>
    <form [formGroup]="form" class="court-request-form">
      <p class="intro">
        Correct a court's details or tell us about one that is missing. Our team reviews every request before
        the court list changes.
      </p>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Which court?</mat-label>
        <mat-select [formControl]="courtChoice" (selectionChange)="onCourtChoice($event.value)">
          @for (court of courts; track court.id) {
            <mat-option [value]="court.id">{{ court.name }}</mat-option>
          }
          <mat-option [value]="NEW_COURT_CHOICE" class="new-court-option">
            <mat-icon fontSet="fa" fontIcon="fa-plus"></mat-icon>
            My court isn't listed
          </mat-option>
        </mat-select>
        <mat-hint>
          @if (isUpdate) {
            Edit the fields that are wrong — only what you change is sent.
          } @else {
            Tell us about the missing court.
          }
        </mat-hint>
      </mat-form-field>

      @if (hasChoice) {
        <section class="field-section" aria-labelledby="court-request-details">
          <h3 id="court-request-details" class="section-title">Court</h3>
          <div class="field-grid">
            <mat-form-field appearance="outline" class="span-6">
              <mat-label>Court name</mat-label>
              <input matInput formControlName="name" />
              @if (form.get('name')?.hasError('required')) {
                <mat-error>A court name is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-3">
              <mat-label>Court type</mat-label>
              <mat-select formControlName="type">
                @for (type of courtTypes; track type) {
                  <mat-option [value]="type">{{ type }}</mat-option>
                }
              </mat-select>
              @if (form.get('type')?.hasError('required')) {
                <mat-error>A court type is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-3">
              <mat-label>Phone number</mat-label>
              <input matInput formControlName="phone_number" mask="(000) 000-0000" placeholder="(555) 555-5555" />
            </mat-form-field>
          </div>
        </section>

        <section class="field-section" aria-labelledby="court-request-address">
          <h3 id="court-request-address" class="section-title">Address</h3>
          <div class="field-grid">
            <mat-form-field appearance="outline" class="span-6">
              <mat-label>Street address</mat-label>
              <input matInput formControlName="street_address" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-6">
              <mat-label>Street address 2</mat-label>
              <input matInput formControlName="street_address_2" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-3">
              <mat-label>City</mat-label>
              <input matInput formControlName="city" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-1">
              <mat-label>State</mat-label>
              <input matInput formControlName="state" maxlength="2" />
              @if (form.get('state')?.hasError('required')) {
                <mat-error>A state is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>ZIP code</mat-label>
              <input matInput formControlName="zip_code" />
            </mat-form-field>
          </div>
        </section>

        <section class="field-section" aria-labelledby="court-request-jurisdiction">
          <h3 id="court-request-jurisdiction" class="section-title">Jurisdiction</h3>
          <div class="field-grid">
            @if (!isUpdate) {
              <mat-form-field appearance="outline" class="span-6">
                <mat-label>County or venue</mat-label>
                <input matInput formControlName="county_or_venue" />
                <mat-hint>Helps us link the new court to the right county.</mat-hint>
                @if (form.get('county_or_venue')?.hasError('required')) {
                  <mat-error>A county or venue is required</mat-error>
                }
              </mat-form-field>
            }

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Precinct</mat-label>
              <input matInput formControlName="precinct" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>Place</mat-label>
              <input matInput formControlName="place" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="span-2">
              <mat-label>District</mat-label>
              <input matInput formControlName="district" />
            </mat-form-field>
          </div>
        </section>

        <section class="field-section" aria-labelledby="court-request-source">
          <h3 id="court-request-source" class="section-title">Source</h3>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Source URL</mat-label>
            <input matInput formControlName="source_url" placeholder="https://" />
            <mat-hint>A link to the court website or official source that backs up this request.</mat-hint>
            @if (form.get('source_url')?.hasError('required')) {
              <mat-error>A source URL is required</mat-error>
            }
            @if (form.get('source_url')?.hasError('url')) {
              <mat-error>Enter a full URL starting with http:// or https://</mat-error>
            }
          </mat-form-field>
        </section>
      }

      @if (isUpdate && !!form.get('target_court_id')?.value && !canSubmit && !submitting) {
        <p class="change-hint">Change at least one field to request an update.</p>
      }

      @if (errorMessage) {
        <p class="error-message" role="alert">{{ errorMessage }}</p>
      }
    </form>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button mat-dialog-close [disabled]="submitting">Cancel</button>
    <button mat-flat-button color="accent" [disabled]="!canSubmit" (click)="submit()">
      @if (submitting) {
        <mat-spinner class="submit-spinner" diameter="18"></mat-spinner>
      } @else {
        Submit for review
      }
    </button>
  </mat-dialog-actions>
} @else {
  <mat-dialog-content>
    <div class="confirmation">
      <mat-icon fontSet="fa" fontIcon="fa-circle-check" class="confirmation-icon"></mat-icon>
      <h3 class="confirmation-title">Request submitted for review</h3>
      <p class="confirmation-body">{{ confirmationMessage }}</p>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-flat-button color="accent" (click)="done()">Done</button>
  </mat-dialog-actions>
}
